import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import './App.css';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import ReportForm from './pages/ReportForm';
import HistoryPage from './pages/HistoryPage';
import ReportView from './pages/ReportView';
import AdminPanel from './pages/AdminPanel';
import AccessibilityPanel from './components/AccessibilityPanel';
import MobileNav from './components/MobileNav';
import ConsignesPage from './pages/ConsignesPage';
import { clearStudentsCache } from './lib/consignesCache';
import ChangePassword from './components/ChangePassword';
import { applyAccessibilityPrefs, loadAccessibilityPrefs } from './lib/accessibility';
import { desktopApi } from './lib/desktopApi';
import { clearLocalDrafts, listLocalDrafts, listReportPhotos } from './lib/offlineStore';
import { startSyncEngine } from './lib/syncEngine';

// Agent Context
const AgentContext = createContext();

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within AgentProvider');
  }
  return context;
};

function readSavedAgent() {
  try {
    const saved = localStorage.getItem('currentAgent');
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    localStorage.removeItem('currentAgent');
    return null;
  }
}

const AgentProvider = ({ children }) => {
  const [currentAgent, setCurrentAgent] = useState(readSavedAgent);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    desktopApi.getAuthConfig().catch(() => null).finally(() => {
      if (cancelled) return;
      if (!localStorage.getItem('agentToken')) {
        setCurrentAgent(null);
        localStorage.removeItem('currentAgent');
      }
      if (!cancelled) setAuthReady(true);
    });
    startSyncEngine();
    return () => { cancelled = true; };
  }, []);

  const login = (agent, token) => {
    const safeAgent = {
      id: agent.id,
      name: agent.name,
      active: agent.active,
      mustChangePassword: Boolean(agent.must_change_password || agent.mustChangePassword),
      isAdmin: Boolean(agent.is_admin || agent.isAdmin),
    };
    setCurrentAgent(safeAgent);
    localStorage.setItem('currentAgent', JSON.stringify(safeAgent));
    if (token) localStorage.setItem('agentToken', token);
    else localStorage.removeItem('agentToken');
  };

  const clearMustChangePassword = () => {
    setCurrentAgent((agent) => {
      if (!agent) return agent;
      const next = { ...agent, mustChangePassword: false };
      localStorage.setItem('currentAgent', JSON.stringify(next));
      return next;
    });
  };

  const logout = async () => {
    const agentId = currentAgent?.id;
    let pending = false;
    try {
      const drafts = await listLocalDrafts();
      const photos = agentId ? await listReportPhotos(agentId) : [];
      pending = Boolean((drafts && drafts.length) || (photos && photos.length));
    } catch {
      pending = false;
    }
    if (pending && typeof window.confirm === 'function') {
      const erase = window.confirm(
        "Un brouillon ou des photos sont encore sur cet appareil. Annuler pour rester connecté et les envoyer d'abord. OK pour les effacer et se déconnecter."
      );
      if (!erase) return false;
      try {
        await clearLocalDrafts();
      } catch {
        /* La session se ferme quand même. */
      }
    }
    if (localStorage.getItem('agentToken')) {
      desktopApi.logoutAgent().catch(() => null);
    }
    clearStudentsCache();
    setCurrentAgent(null);
    localStorage.removeItem('currentAgent');
    localStorage.removeItem('agentToken');
    return true;
  };

  return (
    <AgentContext.Provider value={{ currentAgent, authReady, login, logout, clearMustChangePassword }}>
      {children}
    </AgentContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { currentAgent, authReady, clearMustChangePassword, logout } = useAgent();

  if (!authReady) {
    return <div className="safe-screen min-h-screen bg-background" />;
  }

  if (!currentAgent) {
    return <Navigate to="/" replace />;
  }

  if (currentAgent.mustChangePassword) {
    return (
      <div className="min-h-[100dvh] bg-background pt-[calc(7.5rem+var(--sat))] pb-10 pl-[calc(1rem+var(--sal))] pr-[calc(1rem+var(--sar))]">
        <ChangePassword forced onDone={clearMustChangePassword} onLogout={logout} />
      </div>
    );
  }
  
  return children;
};

const MobileNavGate = () => {
  const { pathname } = useLocation();
  const { currentAgent } = useAgent();
  if (pathname === '/') return null;
  if (currentAgent?.mustChangePassword) return null;
  if (!currentAgent && !pathname.startsWith('/admin')) return null;
  return <MobileNav />;
};

function App() {
  useEffect(() => {
    applyAccessibilityPrefs(loadAccessibilityPrefs());
  }, []);

  return (
    <AgentProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/nouveau-rapport"
              element={
                <ProtectedRoute>
                  <ReportForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/report/edit/:reportId"
              element={
                <ProtectedRoute>
                  <ReportForm editMode={true} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/historique"
              element={
                <ProtectedRoute>
                  <HistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rapport/:reportId"
              element={
                <ProtectedRoute>
                  <ReportView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consignes/:sectionId?"
              element={
                <ProtectedRoute>
                  <ConsignesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={<AdminPanel />}
            />
          </Routes>
          <MobileNavGate />
          <AccessibilityPanel />
        </BrowserRouter>
        <Toaster
          position="top-right"
          richColors
          offset={{
            top: 'calc(var(--sat) + 24px)',
            right: 'calc(var(--sar) + 24px)',
            bottom: '24px',
            left: 'calc(var(--sal) + 24px)',
          }}
          mobileOffset={{
            top: 'calc(var(--sat) + 16px)',
            right: 'calc(var(--sar) + 16px)',
            bottom: '16px',
            left: 'calc(var(--sal) + 16px)',
          }}
        />
        <footer className="hidden md:flex fixed bottom-0 left-[var(--sal)] right-[var(--sar)] h-9 items-center justify-center px-4 text-center text-xs text-muted-foreground bg-background/80 backdrop-blur-sm border-t border-border/30 z-40">
          Application développée par Luca Labate
        </footer>
      </div>
    </AgentProvider>
  );
}

export default App;